import { FiLayers } from 'react-icons/fi';

export const pastPerfectData = {
  courseId: 'past-perfect',
  courseType: 'grammar',
  title: 'Past Perfect & Narratives',
  icon: FiLayers,
  overview: {
    impact: 'Show clear time relationships in past narratives',
    focus: 'had + past participle for the \'earlier past\'',
    uses: 'Speaking Part 2, Writing narratives',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Formation & Use',
      content: `<p><strong>had + past participle</strong> — the past of the past.</p><p>When you're already talking about the past and need to refer to something EVEN EARLIER:</p>`,
      examples: [
        {
          label: 'Sequence',
          text: 'When I arrived, the film had already started.'
        },
        {
          label: 'Experience',
          text: 'I had never seen snow before I visited Canada.'
        },
        {
          label: 'Cause',
          text: 'She was tired because she had worked all day.'
        }
      ]
    },
    {
      title: 'Past Perfect vs Past Simple',
      content: `<p>Use past perfect to show which event happened FIRST:</p>`,
      examples: [
        {
          label: 'Two past simples',
          text: 'I ate and (then) left. (Unclear order or sequential)'
        },
        {
          label: 'With past perfect',
          text: 'After I had eaten, I left. (Clear: eating happened first)'
        }
      ],
      points: [
        'Use past perfect for the EARLIER action',
        'Use past simple for the LATER/main action',
        'Signal words: after, before, by the time, already, never...before, when'
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Tense',
      type: 'select',
      instructions: 'Past simple or past perfect?',
      questions: [
        {
          text: 'By the time we arrived, the show ___ (start).',
          options: [
            'had started',
            'started',
            'has started'
          ],
          answer: 'had started',
          explanation: 'The show started BEFORE arrival → past perfect for the earlier action.'
        },
        {
          text: 'She ___ (never / fly) before her trip to Australia.',
          options: [
            'had never flown',
            'never flew',
            'has never flown'
          ],
          answer: 'had never flown',
          explanation: 'Experience before a past event → past perfect.'
        },
        {
          text: 'After he ___ (finish) his homework, he went to bed.',
          options: [
            'had finished',
            'finished',
            'has finished'
          ],
          answer: 'had finished',
          explanation: 'Finished homework BEFORE going to bed → past perfect for earlier action.'
        }
      ]
    }
  ],
  tips: [
    'Past perfect = the \'earlier past\' (before another past event)',
    'Signal words: after, before, by the time, already, just, never...before',
    'Don\'t overuse — only when showing time order matters',
    'Excellent for vivid Speaking Part 2 narratives'
  ],
  dailyPractice: [
    'Practice past perfect & narratives concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Past Perfect: had + past participle for actions before another past event. Shows clear time relationships in IELTS narratives.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...pastPerfectData,
      title: pastPerfectData.title + ' (Foundations)',
      overview: { ...pastPerfectData.overview, duration: '15-20 minutes' },
      sections: pastPerfectData.sections.slice(0, Math.max(2, Math.ceil(pastPerfectData.sections.length / 2))),
      exercises: pastPerfectData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...pastPerfectData,
      title: pastPerfectData.title + ' (Advanced)',
      overview: { ...pastPerfectData.overview, duration: '30-35 minutes' },
      sections: pastPerfectData.sections,
      exercises: pastPerfectData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(pastPerfectData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return pastPerfectData;
}
